import 'formdata-polyfill';
import axios from 'axios';
import loadHandler from './helpers/loadHandler';
import Observer from 'common/scripts/observer';
import router from './router';
import {Utils} from 'common/scripts/utils';
import Vue from 'vue';
import Vuex from 'vuex';

const observer = new Observer();

Vue.use(Vuex);

const url = process.env.NODE_ENV === 'development' ? '/tests/visual/searchvisual_test.php' : '/ajax/searchvisual.php';

export default new Vuex.Store({
    /**
     * Глобальное хранилище для всего приложения
     * Доступ только через геттеры, изменение через мутацию.
     */
    state: {
        data: {
            render     : '',
            elements   : '',
            breadcrumbs: ''
        },
        canvasSize: {
            width : 0,
            height: 0
        },
        slice         : true,
        filters       : {},
        pointers      : [],
        fakePointers  : [],
        dataStep      : {},
        form          : '',
        formData      : '',
        rotate        : false,
        isProc        : false,
        isReady       : false,
        parametricLink: '',
        plan          : {
            src: ''
        },
        backLink  : '',
        objectLogo: {
            href  : '',
            source: '',
            name  : ''
        },
        path                   : '',
        isRedirect             : false,
        isBuilding             : false,
        isFlat                 : false,
        isInnerVisual          : false,
        isProfitbase           : false,
        isLoadedPostMessageSend: false,
        redirectStep           : null
    },

    /**
     * Методы для получения данных из стейта.
     */
    getters: {
        GET_RENDER(state) {
            return state.data.render;
        },

        GET_ELEMENTS(state) {
            return state.data.elements;
        },

        GET_CANVAS_SIZE(state) {
            return state.canvasSize;
        },

        GET_FILTERS(state) {
            return state.filters;
        },

        GET_POINTERS(state) {
            return state.pointers;
        },

        GET_FAKEPOINTERS(state) {
            return state.fakePointers;
        },

        GET_BREADCRUMBS(state) {
            return state.data.breadcrumbs;
        },

        GET_DATA_STEP(state) {
            return state.dataStep;
        },

        GET_FORM_DATA(state) {
            return state.formData;
        },

        GET_FORM(state) {
            return state.form;
        },

        GET_ROTATE(state) {
            return state.rotate;
        },

        GET_IS_PROCESSING(state) {
            return state.isProc;
        },

        GET_IS_READY(state) {
            return state.isReady;
        },

        /**
         * Возвращает ссылку на параметрический поиск
         * @param {object} state - текущий стейт
         * @return {string} - ссылка на параметрический поиск
         */
        GET_PARAMETRIC_LINK(state) {
            return state.parametricLink;
        },

        /**
         * Геттер, забирает общий план для шага выбора квартиры
         * @param {object} state - текущий стейт
         * @return {object} - общий план
         */
        GET_PLAN(state) {
            return state.plan;
        },

        /**
         * Возвращает ссылку назад
         * @param {object} state - текущий стейт
         * @return {string} - ссылка на объект (или куда скажут)
         */
        GET_BACK_LINK(state) {
            return state.backLink;
        },

        /**
         * Возвращает объект с логотипом объекта
         * @param {object} state - текущий стейт
         * @return {object} - объект с логотипом объекта
         */
        GET_OBJECT_LOGO(state) {
            return state.objectLogo;
        },

        /**
         * Возвращает, надо ли вписывать фоновое изображение в весь экран
         * @param {object} state - текущий стейт
         * @return {Boolean} - true, если надо, false, если нет
         */
        GET_SLICE(state) {
            return state.slice;
        },

        GET_PATH(state) {
            return state.path;
        },

        GET_REDIRECT(state) {
            return state.isRedirect;
        },

        GET_SKIP_STEP(state) {
            return state.redirectStep;
        },

        GET_IS_FLAT(state) {
            return state.isFlat;
        },

        GET_IS_FLOOR(state) {
            return state.dataStep.step === 'floor';
        },

        GET_VISUAL_VERSION(state) {
            return state.isInnerVisual;
        },

        GET_PROFITBASE(state) {
            return state.isProfitbase;
        },

        GET_LOADED_POSTMESSAGE(state) {
            return state.isLoadedPostMessageSend;
        }
    },

    /**
     * События которые введут за собой изменение стейта.
     * https://vuex.vuejs.org/ru/guide/actions.html
     */
    actions: {
        send(context, {body, resetForm = false, query = {}}) {
            context.commit('SET_IS_PROCESSING', true);
            context.commit('SET_VISUAL_VERSION', query);
            context.commit('SET_PROFITBASE', query);
            context.commit('SET_DATA_STEP', body);
            context.commit('SET_FORM_DATA', {
                type     : 'init',
                resetForm: resetForm ? context.commit('SET_FORM', '') : resetForm,
                query
            });

            return new Promise((resolve) => {
                axios.post(url, this.getters.GET_FORM_DATA)
                    .then((response) => {
                        const status = response.data.request.result;
                        const errors = response.data.request.errors;

                        if (!status) {
                            throw new Error(errors.length ? errors : 'Ошибка на сервере');
                        }

                        if (errors && errors.length) {
                            errors.forEach((error) => {
                                console.error(error);
                            });
                        }

                        const data = response.data.data;
                        const render = data.render.link;

                        // проскок корпусов
                        context.commit('SET_SKIP_STEP', data);
                        context.dispatch('checkRedirect', data);

                        if (!this.getters.GET_REDIRECT) {
                            loadHandler(render)
                                .then((size) => {
                                    context.commit('SET_CANVAS_SIZE', size);
                                    context.commit('SET_DATA', data);
                                    context.commit('SET_POINTERS', data);
                                    context.commit('SET_FAKEPOINTERS', data);
                                    context.commit('SET_FILTERS', data);
                                    context.commit('SET_ROTATE', data);
                                    context.commit('SET_PARAMETRIC_LINK', data);
                                    context.commit('SET_PLAN', data);
                                    context.commit('SET_BACK_LINK', data);
                                    context.commit('SET_OBJECT_LOGO', data);
                                    context.commit('SET_SLICE', body);
                                    context.commit('SET_IS_PROCESSING', false);
                                    context.commit('SET_IS_READY');
                                    observer.publish('sendReqToLocalhost');
                                    resolve();
                                });
                        }
                    })
                    .catch((error) => {
                        console.error(`При получении данных произошла ошибка ${error}`);
                        throw new Error();
                    });
            });
        },

        changeFilter(context, {form, query = {}}) {
            context.commit('SET_FORM', form);
            context.commit('SET_VISUAL_VERSION', query);
            context.commit('SET_PROFITBASE', query);
            context.commit('SET_FORM_DATA', {
                type     : 'filter',
                resetForm: false,
                query
            });
            context.commit('SET_IS_PROCESSING', true);

            axios.post(url, context.getters.GET_FORM_DATA)
                .then((response) => {
                    const data = response.data.data;

                    context.commit('SET_ELEMENTS', data);
                    context.commit('SET_PARAMETRIC_LINK', data);
                    context.commit('SET_POINTERS', data);
                    context.commit('SET_FAKEPOINTERS', data);
                    context.commit('SET_FILTERS', data);
                    context.commit('SET_IS_PROCESSING', false);
                })
                .catch((error) => {
                    console.error(`При фильтрации произошла ошибка= ${error}`);
                });
        },

        changePosition(context, data) {
            context.commit('SET_POSITION_ELEMENTS', data);
        },

        changeStateLoading(context, state) {
            context.commit('SET_IS_PROCESSING', state);
        },

        checkRedirect(context, data) {
            const activeElements = data.elements.filter((element) => {
                return !element.disabled;
            });

            // условия редиректа:
            if (this.getters.GET_IS_FLAT) {
                // дошли до уровня квартиры - некуда редиректиться
                context.commit('SET_REDIRECT', false);
            } else if (!this.getters.GET_SKIP_STEP && activeElements.length !== 1) {
                 // в данных нет указания редиректиться
                 context.commit('SET_REDIRECT', false);
            } else if (this.getters.GET_SKIP_STEP && this.getters.GET_IS_FLOOR) {
                 // дошли до уровня комплекса, не надо проваливаться дальше
                 context.commit('SET_REDIRECT', false);
            } else {
                // в противном случае - редиректимся
                context.commit('SET_REDIRECT', true);

                const startPath = location.pathname
                    .replace(`${this.getters.GET_PATH}`, '')
                    .replace('new_visual_debug', '')
                    .replace('visual', '')
                    .replace('//', '/');

                let path = '';

                if (this.getters.GET_SKIP_STEP) {
                    let activeElementsString = '';

                    activeElements.forEach((elem, index) => {
                        const elemArray = elem.link.split('/');
                        const elemId = elemArray[1];
                        const elemTitle = elemArray[0];

                        if (activeElementsString.includes(elemTitle)) {
                            activeElementsString += `-${elemId}`;
                        } else {
                            activeElementsString += `${elemTitle}/${elemId}`;
                        }
                    });

                    if (startPath.toString().includes('building') && activeElementsString.includes('building')) {
                        // костыль для возврата на уровень птички через хлебные крошки
                        path = `/${activeElementsString}`
                            .replace('//', '/');
                    } else {
                        path = `${startPath}${this.getters.GET_PATH}${activeElementsString}`
                            .replace('//', '/');
                    }

                } else {
                    path = `${startPath}${this.getters.GET_PATH}${activeElements[0].link}`
                        .replace('//', '/')
                        .replace('/parking/parking', '/parking')
                        .replace('/commerce/commerce', '/commerce')
                        .replace('//', '/');
                }

                const query = location.search
                    .replace(/(^\?)/, '')
                    .split('&')
                    // eslint-disable-next-line func-names
                    .map(function (n) {
                        return n = n.split('='), this[n[0]] = n[1], this;
                    }
                        .bind({}))[0];

                router.replace({
                    path,
                    query
                });

            }
        },

        setProfitBaseLoaded(context) {
            context.commit('SET_LOADED_POSTMESSAGE');
        }
    },

    /**
     * Методы для изменения стейта.
     */
    mutations: {
        SET_CANVAS_SIZE(state, data) {
            state.canvasSize = data;
        },

        SET_DATA(state, data) {
            state.data = data;
        },

        SET_ELEMENTS(state, data) {
            state.data.elements = data.elements;
        },

        SET_POINTERS(state) {
            const elements = state.data.elements;
            const pointers = [];

            if (elements.length) {
                elements.forEach((element) => {
                    if (element.pointer) {
                        element.pointer.id = element.id;
                        element.pointer.link = element.link;
                        pointers.push(element.pointer);
                    }
                });
            }

            state.pointers = pointers;
        },

        SET_FAKEPOINTERS(state) {
            if (!state.data.points) {
                return;
            }

            const points = state.data.points;
            const pointers = [];

            points.forEach((point) => {
                pointers.push(point);
            });

            state.fakePointers = pointers;
        },

        SET_FILTERS(state, data) {
            state.filters = data.filters || {};
        },

        SET_DATA_STEP(state, data) {
            state.dataStep = data;
            observer.publish('dataStep', state.dataStep.step);
        },

        SET_FORM(state, form) {
            state.form = form;
        },

        SET_FORM_DATA(state, {type, resetForm = false, query = {}}) {
            const form = this.getters.GET_FORM;
            const dataStep = this.getters.GET_DATA_STEP;

            state.formData = form && !resetForm ? new FormData(form) : new FormData();

            for (const item in dataStep) {
                if (Utils.keyExist(dataStep, item)) {
                    if (!state.formData.has(item)) {
                        state.formData.append(item, dataStep[item]);
                    }
                }
            }

            /**
             * query - данные из строки поиска (get-параметры)
             */
            for (const item in query) {
                if (Utils.keyExist(query, item)) {
                    if (!state.formData.has(item)) {
                        state.formData.append(item, query[item]);
                    }

                    // проверка, является ли это визуальный с внутренних страниц
                    if (item === 'innerVisual') {
                        state.isInnerVisual = true;
                    }

                    if (item === 'profitbase') {
                        state.isProfitbase = true;
                    }
                }
            }

            state.formData.append('type', type);
        },

        SET_VISUAL_VERSION(state, query) {
            for (const item in query) {
                if (item === 'innerVisual') {
                    state.isInnerVisual = true;
                }
            }
        },

        SET_PROFITBASE(state, query) {
            for (const item in query) {
                if (item === 'profitbase') {
                    state.isProfitbase = true;
                }
            }
        },

        SET_ROTATE(state, data) {
            state.rotate = data.rotate;
        },

        SET_IS_PROCESSING(state, value) {
            state.isProc = value;
        },

        SET_IS_READY(state) {
            state.isReady = true;
        },

        SET_POSITION_ELEMENTS(state, data) {
            const elements = state.pointers;
            const fakeElements = state.fakePointers;

            if (elements) {
                elements.forEach((item, index) => {
                    item.reposition = data.coords[index];
                });
            }

            if (fakeElements) {
                fakeElements.forEach((item, index) => {
                    item.reposition = data.coords[index];
                });
            }
        },

        /**
         * Мутация устанавливает ссылку на параметрический поиск
         * @param {object} state - текущий стейт
         * @param {object} data - данные от сервера
         */
        SET_PARAMETRIC_LINK(state, data) {
            state.parametricLink = data.parametricLink;
        },

        /**
         * Мутация устанавливает план комплекса
         * @param {object} state - текущий стейт
         * @param {object} data - данные от сервера
         */
        SET_PLAN(state, data) {
            state.plan = data.plan;
        },

        SET_BACK_LINK(state, data) {
            state.backLink = data.backLink;
        },

        SET_OBJECT_LOGO(state, data) {
            state.objectLogo = data.objectLogo;
        },

        /**
         * Мутация определяет, вписывать ли свг в экран
         * @param {object} state - текущий стейт
         * @param {object} body - текущий шаг
         */
        SET_SLICE(state, body) {
            state.slice = body.step !== 'flat';
        },

        SET_PATH(state, addPath) {
            state.path = addPath;
        },

        SET_REDIRECT(state, redirect) {
            state.isRedirect = redirect;
        },

        SET_IS_FLAT(state, value) {
            state.isFlat = value;
        },

        SET_LOADED_POSTMESSAGE(state) {
            state.isLoadedPostMessageSend = true;
        },

        SET_SKIP_STEP(state, data) {
            if (data.redirectStep) {
                state.redirectStep = data.redirectStep;
            }
        }
    }
});
